// Aura placement geometry. Pure. No React, no Three.js, no DOM.
//
// Sorcery's auras come in three placement shapes. Most live at the
// intersection of four cells (a 2×2 "affected area"); a few wall-type
// cards live on a single site's edge (border between two cells); a
// handful are conjured atop a single site (overlay on one cell).
//
// This module owns:
//   • The shape vocabulary + per-card overrides for the non-default
//     cases (walls and atop-site cards).
//   • The legal-anchor enumerators per shape. The drag halo system
//     calls these to render highlights.
//   • The nearest-anchor snap functions. The drop-commit calls these
//     to anchor the dropped card.
//   • The anchor→world geometry. Given an abstract anchor, return
//     the XYZ where the aura should physically sit.
//
// The "anchor" abstraction is shape-specific:
//   INTERSECTION_4: { col, row } where the anchor is the
//                   top-left corner of cell (col, row). I.e. the
//                   corner shared by (col-1,row-1), (col,row-1),
//                   (col-1,row), (col,row). Cells covered: those four.
//                   Legal col ∈ [1, totalCols-1], row ∈ [1, totalRows-1].
//   INTERSECTION_2: { col, row, edge: 'top'|'right'|'bottom'|'left' }
//                   The midpoint of one edge of cell (col, row).
//                   The wall covers cells on either side of that edge.
//   ATOP_SITE:      { col, row }. Same as a site cell. The aura
//                   overlays the existing site.
//
// Adding a new non-default aura → append a registry entry below. The
// halo / snap / drop pipelines all read from `getAuraShape`.

import { sampleCellGeometry } from './locations';

export const AURA_SHAPE = Object.freeze({
  INTERSECTION_4: 'intersection_4',
  INTERSECTION_2: 'intersection_2',
  ATOP_SITE:      'atop_site',
  // Like ATOP_SITE but inverted. The aura must be placed on a VOID
  // cell (no site of any kind, including Rubble). Sow the Earth: the
  // aura plays the controller's top atlas site to that cell as part
  // of its on_play, then the aura sits atop the freshly-spawned site.
  ATOP_VOID:      'atop_void',
});

// Per-card overrides. Cards not in this registry default to
// INTERSECTION_4 (the dominant case. "affects a 2×2 area").
//
// `siteFilter` is consulted by the legal-anchor enumerator for
// ATOP_SITE shapes: a card with `rarity: ['Elite', 'Unique']` only
// considers sites whose catalog rarity is in that list.
const AURA_SHAPE_REGISTRY = {
  // Walls live on the border of a controlled site (codex: "Conjure
  // atop the border of a site you control"). No card-side filter
  // beyond the controller check baked into the enumerator.
  'sorcery-wall_of_air':      { shape: AURA_SHAPE.INTERSECTION_2 },
  'sorcery-wall_of_fire':     { shape: AURA_SHAPE.INTERSECTION_2 },
  'sorcery-wall_of_ice':      { shape: AURA_SHAPE.INTERSECTION_2 },
  'sorcery-wall_of_brambles': { shape: AURA_SHAPE.INTERSECTION_2 },

  // Atop-site auras. Conjured on a single site. Optional rarity
  // filter narrows the legal anchor set. cardIds come from the
  // normalizer (raw.name → lower → /[^a-z0-9]+/g → "_"), so the
  // apostrophe in "Castle's"/"Hamlet's" becomes an underscore.
  'sorcery-wildfire':         { shape: AURA_SHAPE.ATOP_SITE },
  'sorcery-castle_s_ablaze':  { shape: AURA_SHAPE.ATOP_SITE, siteFilter: { rarity: ['Elite', 'Unique'] } },
  'sorcery-hamlet_s_ablaze':  { shape: AURA_SHAPE.ATOP_SITE, siteFilter: { rarity: ['Ordinary', 'Exceptional'] } },
  'sorcery-salt_the_earth':   { shape: AURA_SHAPE.ATOP_SITE },

  // Atop-void auras. Conjured on a cell with no site.
  'sorcery-sow_the_earth':    { shape: AURA_SHAPE.ATOP_VOID },
};

/**
 * Return the placement-shape descriptor for an aura card. Catalog
 * rows OR card instances both work as inputs. Both carry a cardId
 * (instance) or unique_id (catalog) the registry keys against.
 *
 * @param {object} card  - catalog row or instance
 * @returns {{ shape: string, siteFilter?: object }}
 */
export function getAuraShape(card) {
  if (!card) return { shape: AURA_SHAPE.INTERSECTION_4 };
  const id = card.cardId || card.unique_id;
  return AURA_SHAPE_REGISTRY[id] || { shape: AURA_SHAPE.INTERSECTION_4 };
}

/**
 * Reverse-resolve a placed aura's anchor from its world position.
 *
 * The drag-time snap writes the anchor point onto `card.x, card.z`
 * but doesn't persist the anchor's grid coordinates. This helper
 * recovers `{ col, row, edge? }` from that point, given the card's
 * stamped `_auraShape`.
 *
 * Pure. No React, no Three.js. Returns `null` if the card isn't on a
 * recognizable anchor (degenerate grid, raw drop point, etc.).
 *
 * @param {object} card
 * @param {object} grid
 * @param {number} totalCols
 * @param {number} totalRows
 * @param {(x: number, z: number) => { col, row } | null} cellAt
 */
export function resolveAuraAnchor(card, grid, totalCols, totalRows, cellAt) {
  if (!card || typeof card.x !== 'number' || typeof card.z !== 'number') return null;
  const shape = card._auraShape || getAuraShape(card);
  if (shape.shape === AURA_SHAPE.ATOP_SITE || shape.shape === AURA_SHAPE.ATOP_VOID) {
    const cell = cellAt?.(card.x, card.z);
    return cell ? { col: cell.col, row: cell.row } : null;
  }
  if (shape.shape === AURA_SHAPE.INTERSECTION_4) {
    const found = nearestAura4Anchor(grid, totalCols, totalRows, card.x, card.z);
    return found ? { col: found.col, row: found.row } : null;
  }
  if (shape.shape === AURA_SHAPE.INTERSECTION_2) {
    // Walk every site-edge anchor, pick the one whose midpoint matches.
    // We can't use legalAura2Anchors (it filters to controlled sites).
    // the aura might span an edge whose neighbor became opponent-
    // controlled mid-game. Instead, walk all 4 edges of every cell the
    // aura's point sits adjacent to.
    if (!cellAt) return null;
    const here = cellAt(card.x, card.z);
    if (!here) return null;
    const candidates = [];
    for (const baseCell of [here, { col: here.col - 1, row: here.row }, { col: here.col + 1, row: here.row }, { col: here.col, row: here.row - 1 }, { col: here.col, row: here.row + 1 }]) {
      if (baseCell.col < 0 || baseCell.row < 0 || baseCell.col >= totalCols || baseCell.row >= totalRows) continue;
      for (const edge of ['top', 'right', 'bottom', 'left']) {
        const p = aura2AnchorWorldPoint(grid, baseCell.col, baseCell.row, edge);
        if (!p) continue;
        const d2 = (p.x - card.x) ** 2 + (p.z - card.z) ** 2;
        candidates.push({ col: baseCell.col, row: baseCell.row, edge, d2 });
      }
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => a.d2 - b.d2);
    const best = candidates[0];
    return { col: best.col, row: best.row, edge: best.edge };
  }
  return null;
}

/**
 * Cells covered by an anchor, as `col,row` string keys.
 *
 *   INTERSECTION_4: 4 cells around the corner (col, row). I.e. the
 *     anchor is the top-left corner of cell (col, row), and the four
 *     covered cells are (col-1, row-1), (col, row-1), (col-1, row),
 *     (col, row).
 *   ATOP_SITE: a single cell (col, row).
 *   INTERSECTION_2: 2 cells on either side of the edge. (col, row)
 *     plus the neighbour across the named edge.
 *
 * Used by the drag-halo system: each anchor projects to a set of grid
 * cells that should brighten as "the cells this aura would cover if
 * dropped here." The cell-halo overlay reads this set to pick which
 * cells to render as active.
 */
export function anchorCoveredCells(anchor, shape) {
  if (!anchor) return [];
  if (shape === AURA_SHAPE.INTERSECTION_4) {
    const { col, row } = anchor;
    return [
      `${col - 1},${row - 1}`,
      `${col},${row - 1}`,
      `${col - 1},${row}`,
      `${col},${row}`,
    ];
  }
  if (shape === AURA_SHAPE.ATOP_SITE || shape === AURA_SHAPE.ATOP_VOID) {
    return [`${anchor.col},${anchor.row}`];
  }
  if (shape === AURA_SHAPE.INTERSECTION_2) {
    const { col, row, edge } = anchor;
    let other;
    if (edge === 'top')    other = { col, row: row - 1 };
    else if (edge === 'right')  other = { col: col + 1, row };
    else if (edge === 'bottom') other = { col, row: row + 1 };
    else if (edge === 'left')   other = { col: col - 1, row };
    return other
      ? [`${col},${row}`, `${other.col},${other.row}`]
      : [`${col},${row}`];
  }
  return [];
}

// ── INTERSECTION_4 (2×2) ─────────────────────────────────────────────

/**
 * All legal 2×2 anchor points for the grid. An anchor at (col, row)
 * means the aura covers cells (col-1, row-1), (col, row-1),
 * (col-1, row), (col, row). Valid col ∈ [1, totalCols-1], row ∈
 * [1, totalRows-1]. Interior corners only, so the 2×2 always fits.
 *
 * For a 5×4 grid that's 4 × 3 = 12 anchors.
 */
export function legalAura4Anchors(totalCols, totalRows) {
  const out = [];
  for (let row = 1; row < totalRows; row++) {
    for (let col = 1; col < totalCols; col++) {
      out.push({ col, row });
    }
  }
  return out;
}

/**
 * World-space position of the 4-cell corner at (col, row). Returns
 * { x, z } at the midpoint of the four surrounding cells' centers, or
 * null when the anchor is on the grid edge.
 */
export function aura4AnchorWorldPoint(grid, col, row) {
  // The corner shared by (col-1, row-1) and (col, row). Sample
  // both cells' geometry and average the meeting point.
  const tl = sampleCellGeometry(grid, col - 1, row - 1); // cell ending at this corner
  if (!tl) return null;
  // The corner is at tl.br (bottom-right of the (col-1, row-1) cell).
  return { x: tl.br.x, z: tl.br.z };
}

/**
 * Find the legal 4-cell anchor whose world position is closest to
 * (x, z). Returns the anchor in cell-index form + its world point.
 * Returns null when the grid has no legal anchors (degenerate grid).
 */
export function nearestAura4Anchor(grid, totalCols, totalRows, x, z) {
  let best = null;
  let bestDist = Infinity;
  for (const a of legalAura4Anchors(totalCols, totalRows)) {
    const p = aura4AnchorWorldPoint(grid, a.col, a.row);
    if (!p) continue;
    const dx = p.x - x;
    const dz = p.z - z;
    const d = dx * dx + dz * dz;
    if (d < bestDist) { bestDist = d; best = { ...a, point: p }; }
  }
  return best;
}

// ── ATOP_SITE ────────────────────────────────────────────────────────

/**
 * Legal atop-site anchors for a card. Walks every site on the board
 * and filters by the registry's `siteFilter` predicate (rarity,
 * controller, ...) when present. Returns one entry per matching site:
 * { cardId, col, row, point }.
 *
 * Defaults to "any controller." Per Sorcery codex, Salt the Earth,
 * Wildfire, Castle's Ablaze, and Hamlet's Ablaze all target ANY site
 * regardless of who controls it. A future card that needs a
 * controller restriction can add `controller: 'self' | 'opponent'`
 * to its registry `siteFilter`.
 *
 * @param {object} card
 * @param {Iterable<[any, { userData: { cardInstance } }]>} meshes
 * @param {boolean} controllerRotated. The caster's rotated flag.
 *   Only consulted when the card's `siteFilter.controller` is set.
 * @param {(cardId: string) => object | null} getFullCard
 * @param {(x: number, z: number) => { col, row } | null} cellAt
 */
export function legalAuraAtopSiteAnchors(card, meshes, controllerRotated, getFullCard, cellAt) {
  const out = [];
  if (!meshes) return out;
  const shape = getAuraShape(card);
  const filter = shape.siteFilter || null;
  for (const [, mesh] of meshes) {
    const site = mesh?.userData?.cardInstance;
    if (!site?.isSite) continue;
    if (filter && !siteMatchesFilter(site, getFullCard?.(site.cardId), filter, controllerRotated)) continue;
    const cell = cellAt?.(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    out.push({ cardId: site.id, col: cell.col, row: cell.row, point: { x: mesh.position.x, z: mesh.position.z } });
  }
  return out;
}

/**
 * Legal atop-void anchors. Every cell on the grid that currently has
 * NO site of any kind (true void). Returns `{ col, row, point }` per
 * cell. The point is the cell's geometric center.
 */
export function legalAuraAtopVoidAnchors(meshes, cellAt, grid, totalCols, totalRows) {
  if (!grid) return [];
  // Build a set of occupied cells (any site present).
  const occupied = new Set();
  for (const [, mesh] of meshes || []) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cell = cellAt?.(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    occupied.add(`${cell.col},${cell.row}`);
  }
  const out = [];
  for (let r = 0; r < totalRows; r++) {
    for (let c = 0; c < totalCols; c++) {
      if (occupied.has(`${c},${r}`)) continue;
      const g = sampleCellGeometry(grid, c, r);
      if (!g) continue;
      // Center = average of 4 corners
      const cx = (g.tl.x + g.tr.x + g.br.x + g.bl.x) / 4;
      const cz = (g.tl.z + g.tr.z + g.br.z + g.bl.z) / 4;
      out.push({ col: c, row: r, point: { x: cx, z: cz } });
    }
  }
  return out;
}

/**
 * Find the legal atop-site anchor closest to (x, z), or null.
 */
export function nearestAuraAtopSiteAnchor(legalAnchors, x, z) {
  let best = null;
  let bestDist = Infinity;
  for (const a of legalAnchors) {
    const dx = a.point.x - x;
    const dz = a.point.z - z;
    const d = dx * dx + dz * dz;
    if (d < bestDist) { bestDist = d; best = a; }
  }
  return best;
}

// ── INTERSECTION_2 (wall) ────────────────────────────────────────────

/**
 * Legal wall anchors. Midpoints of edges of controlled sites. Each
 * site has 4 edges; if two adjacent controlled sites share an edge,
 * the edge is deduped (one anchor per unique edge).
 *
 * An "edge" is keyed by a canonical pair of cells: the cell on each
 * side of the edge. The canonical key sorts the two cells so the
 * same edge from two sites' perspectives collapses to one entry.
 *
 * Returns entries: { col, row, edge: 'top'|'right'|'bottom'|'left', point }.
 * `col, row` is the lower-indexed cell of the pair; `edge` describes
 * which side of THAT cell the wall is on.
 */
export function legalAura2Anchors(meshes, controllerRotated, cellAt, grid) {
  const out = [];
  const seen = new Set();
  if (!meshes || !cellAt || !grid) return out;
  for (const [, mesh] of meshes) {
    const site = mesh?.userData?.cardInstance;
    if (!site?.isSite) continue;
    if (!!site.rotated !== !!controllerRotated) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    for (const edge of ['top', 'right', 'bottom', 'left']) {
      const key = canonicalEdgeKey(cell.col, cell.row, edge);
      if (seen.has(key)) continue;
      seen.add(key);
      const point = aura2AnchorWorldPoint(grid, cell.col, cell.row, edge);
      if (!point) continue;
      out.push({ col: cell.col, row: cell.row, edge, point });
    }
  }
  return out;
}

/**
 * Both endpoints of one edge of cell (col, row). Returns
 * `[{ x, z }, { x, z }]` (corner → corner) or null when the cell
 * sample fails. Used by the wall-halo renderer to project the actual
 * edge line, not just its midpoint, so the visual bar follows the
 * underlying edge with correct perspective.
 */
export function wallEdgeEndpoints(grid, col, row, edge) {
  const g = sampleCellGeometry(grid, col, row);
  if (!g) return null;
  if (edge === 'top')    return [g.tl, g.tr];
  if (edge === 'right')  return [g.tr, g.br];
  if (edge === 'bottom') return [g.bl, g.br];
  if (edge === 'left')   return [g.tl, g.bl];
  return null;
}

/**
 * Build a 4-corner world quad that "thickens" an edge perpendicular
 * to its direction by `halfThickness` world units on each side.
 * Returns the 4 corners as `[[x,z],[x,z],[x,z],[x,z]]` in tl-tr-br-bl
 * order so the projector's clip-path traces them correctly.
 *
 * Used by the wall-halo renderer to draw a bar along an edge.
 */
export function thickenEdge(p1, p2, halfThickness) {
  const dx = p2.x - p1.x;
  const dz = p2.z - p1.z;
  const len = Math.hypot(dx, dz) || 1;
  // 2D perpendicular in XZ plane. Rotate (dx, dz) by 90°.
  const px = -dz / len * halfThickness;
  const pz =  dx / len * halfThickness;
  return [
    [p1.x + px, p1.z + pz], // tl
    [p2.x + px, p2.z + pz], // tr
    [p2.x - px, p2.z - pz], // br
    [p1.x - px, p1.z - pz], // bl
  ];
}

/**
 * World-space midpoint of one edge of cell (col, row). Returns null
 * when the cell sample fails.
 */
export function aura2AnchorWorldPoint(grid, col, row, edge) {
  const g = sampleCellGeometry(grid, col, row);
  if (!g) return null;
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 });
  if (edge === 'top')    return mid(g.tl, g.tr);
  if (edge === 'right')  return mid(g.tr, g.br);
  if (edge === 'bottom') return mid(g.bl, g.br);
  if (edge === 'left')   return mid(g.tl, g.bl);
  return null;
}

/** Find the legal wall anchor closest to (x, z). */
export function nearestAura2Anchor(legalAnchors, x, z) {
  let best = null;
  let bestDist = Infinity;
  for (const a of legalAnchors) {
    const dx = a.point.x - x;
    const dz = a.point.z - z;
    const d = dx * dx + dz * dz;
    if (d < bestDist) { bestDist = d; best = a; }
  }
  return best;
}

// ── Internal ─────────────────────────────────────────────────────────

function canonicalEdgeKey(col, row, edge) {
  // Two cells share an edge; key the edge by the canonical (lower, higher)
  // cell-pair so the same edge from either neighbor's perspective hashes
  // to the same value.
  let other;
  if (edge === 'top')    other = { col, row: row - 1 };
  else if (edge === 'right')  other = { col: col + 1, row };
  else if (edge === 'bottom') other = { col, row: row + 1 };
  else if (edge === 'left')   other = { col: col - 1, row };
  else return `${col},${row}:${edge}`;
  const a = { col, row };
  const aKey = `${a.col},${a.row}`;
  const oKey = `${other.col},${other.row}`;
  return aKey < oKey ? `${aKey}|${oKey}` : `${oKey}|${aKey}`;
}

function siteMatchesFilter(site, fullSite, filter, controllerRotated) {
  if (filter.rarity) {
    const rarity = fullSite?.rarity || site.rarity;
    if (!filter.rarity.includes(rarity)) return false;
  }
  if (filter.element) {
    const els = Array.isArray(fullSite?.elements) ? fullSite.elements : [];
    const want = String(filter.element).toLowerCase();
    if (!els.map((e) => String(e).toLowerCase()).includes(want)) return false;
  }
  if (filter.controller === 'self') {
    if (!!site.rotated !== !!controllerRotated) return false;
  } else if (filter.controller === 'opponent') {
    if (!!site.rotated === !!controllerRotated) return false;
  }
  return true;
}
